import React, { useState, useEffect } from "react";
import styled from "styled-components";
import Card from "../Card/Card";
import { useSelector, useDispatch } from "react-redux";
import format from "date-fns/format";
import theme from "../../theme/theme";
import LoadingBar from "../../components/LoadingBar/LoadingBar";
import { getBenchmarkData } from "../../redux/actions";
import { SET_LOADING } from "../../redux/action-types";
import {
  LineChart,
  Line,
  XAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  YAxis,
  Legend,
} from "recharts";

const Wrapper = styled.header`
  display: flex;
  gap: 20px;

  .recharts-legend-item {
    cursor: pointer;
    border: 1px solid lightgray;
    padding: 0px 10px;
    border-radius: 10px;
    padding-bottom: 2px;
    cursor: pointer;
  }

  .legend-item-0 {
    background-color: white !important;
    border: none !important;
    cursor: default;

    .recharts-surface {
      display: none !important;
    }
  }

  .legend-item-1 {
    background-color: white !important;
    border: none !important;
    cursor: default;
  }

  .inactive {
    display: flex;
    align-items: center;
    border-radius: 15px;
    background-color: #eeeeee;
    padding: 0px 10px;
    cursor: pointer;
    padding-bottom: 1.5px;
  }

  @media (max-width: 480px) {
    display: block;
  }

  .infoSection {
    width: 20%;

    @media (max-width: 480px) {
      width: 100%;
    }

    .heading {
      color: #0d2333;
      margin-bottom: 15px;
      font-size: 20px;
    }

    .accountOverViewBody {
      display: flex;

      @media (max-width: 480px) {
        flex-direction: column;
      }
      .information {
        width: 100%;
        @media (max-width: 480px) {
          width: 100%;
        }

        .optionSelectContainer {
          display: flex;

          .dropDownHolder {
            display: flex;
            gap: 20px;
          }
        }

        .assetRoiInformation {
          width: 100%;
          margin-top: 20px;
          @media (max-width: 480px) {
            gap: 0;
            justify-content: space-between;
          }

          .asset {
            .number {
              display: flex;
              justify-content: center;
              font-size: 30px;
              color: black;
              border-bottom: 1px solid lightgray;
              padding-bottom: 15px;
            }
            .text {
              display: flex;
              justify-content: center;
              margin-top: 20px;
              font-size: 15px;
              color: gray;
            }
          }

          .roi {
            margin-top: 20px;
            .number {
              display: flex;
              justify-content: center;
              font-size: 30px;
              // color: green;
              border-bottom: 1px solid lightgray;
              padding-bottom: 15px;
            }

            .text {
              display: flex;
              justify-content: center;
              margin-top: 20px;
              font-size: 15px;
              color: gray;
            }
          }
        }
      }
    }
  }

  .chartSection {
    width: 80%;

    @media (max-width: 480px) {
      width: 100%;
      margin-top: 20px;
    }

    .heading {
      color: #0d2333;
      margin-bottom: 15px;
      font-size: 20px;
      display: flex;
      justify-content: space-between;
      align-items: center;

      .filterButton {
        border: none;
        background-color: white;
        border-radius: 10px;
        padding: 5px 20px;
        font-size: 12px;
        margin-left: 10px;
        box-shadow: rgba(0, 0, 0, 0.35) 0px 1px 5px;
        cursor: pointer;
      }
    }
    .chart {
      width: 100%;
      .custom-tooltip {
        border-radius: 8px;
        background: #ffffff 0% 0% no-repeat padding-box;
        box-shadow: 0px 0px 10px #00000029;
        padding: 5px 10px;
        font-size: 10px;

        .date {
          text-align: center;
        }

        .labelPortfolio {
          display: flex;
          gap: 10px;
          .circle {
            height: 22px;
            width: 22px;
            border-radius: 50%;
            background-color: #33ccff;
          }
          .value {
            font-size: 17px;
            font-weight: 700;
          }
        }

        .label {
        }
      }
      @media (max-width: 480px) {
        margin-top: 10px;
        width: 100%;
      }
      .asset {
        .number {
          text-align: center;
          font-size: 15px;
          color: gray;
          margin-top: 20px;
          border-bottom: 1px solid lightgray;
          padding-bottom: 40px;
        }
        .text {
          margin-top: 20px;
          font-size: 15px;
          color: gray;
        }
      }
    }
  }
`;

const AccountOverView = () => {
  const dispatch = useDispatch();
  const [accountOverviewState, setAccountOverviewState] = useState();
  const [benchmarkDataState, setBenchmarkDataState] = useState([]);
  const [minPercentage, setMinPercentage] = useState();
  const [maxPercentage, setMaxPercentage] = useState();
  const [minDollar, setMinDollar] = useState();
  const [maxDollar, setMaxDollar] = useState();
  const [yTicksForDailyReturn, setYTicksForDailyReturn] = useState();
  const [isSnp500Visible, setIsSnp500Visible] = useState(false);
  const [isNasdaqVisible, setIsNasdaqVisibleVisible] = useState(false);
  const [isBloombergVisible, setIsBloombergVisible] = useState(false);
  const [isBitcoinVisible, setIsBitcoinVisible] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState("");
  const [isLoaded, setIsLoaded] = useState(true);

  const { accountOverview, benchmarkData, loading } = useSelector(
    (state) => state.user
  );

  useEffect(() => {
    dispatch({ type: SET_LOADING, payload: false });
    setIsLoaded(true);
    if (benchmarkData?.graphValues?.length > 0) {
      let tempData = [];
      let yTickForReturn = [0];
      let currentMonth;
      let tempMinPercentage = Math.min(
        (benchmarkData?.graphValues[0].cdR_Portfolio * 100).toFixed(2),
        (benchmarkData?.graphValues[0].cdR_BTC * 100).toFixed(2),
        (benchmarkData?.graphValues[0].cdR_AGG * 100).toFixed(2),
        (benchmarkData?.graphValues[0].cdR_COMP * 100).toFixed(2),
        (benchmarkData?.graphValues[0].cdR_SPY * 100).toFixed(2)
      );
      let tempMaxPercentage = Math.max(
        (benchmarkData?.graphValues[0].cdR_Portfolio * 100).toFixed(2),
        (benchmarkData?.graphValues[0].cdR_BTC * 100).toFixed(2),
        (benchmarkData?.graphValues[0].cdR_AGG * 100).toFixed(2),
        (benchmarkData?.graphValues[0].cdR_COMP * 100).toFixed(2),
        (benchmarkData?.graphValues[0].cdR_SPY * 100).toFixed(2)
      );
      let tempMinDollar = Math.min(
        (benchmarkData?.graphValues[0].cG_Portfolio).toFixed(2),
        (benchmarkData?.graphValues[0].cG_BTC).toFixed(2),
        (benchmarkData?.graphValues[0].cG_AGG).toFixed(2),
        (benchmarkData?.graphValues[0].cG_COMP).toFixed(2),
        (benchmarkData?.graphValues[0].cG_SPY).toFixed(2)
      );
      let tempMaxDollar = Math.max(
        (benchmarkData?.graphValues[0].cG_Portfolio).toFixed(2),
        (benchmarkData?.graphValues[0].cG_BTC).toFixed(2),
        (benchmarkData?.graphValues[0].cG_AGG).toFixed(2),
        (benchmarkData?.graphValues[0].cG_COMP).toFixed(2),
        (benchmarkData?.graphValues[0].cG_SPY).toFixed(2)
      );
      setMinPercentage(minPercentage);
      benchmarkData?.graphValues.forEach((data) => {
        yTickForReturn.push(
          (data.cdR_SPY * 100).toFixed(2),
          (data.cdR_COMP * 100).toFixed(2),
          (data.cdR_AGG * 100).toFixed(2),
          (data.cdR_BTC * 100).toFixed(2)
        );
        tempData.push({
          date:
            currentMonth !== format(new Date(data.date), "MM/yyyy")
              ? format(new Date(data.date), "MM/dd/yyyy")
              : "",
          fullDate: format(new Date(data.date), "MM/dd/yyyy"),
          SPY: (data.cdR_SPY * 100).toFixed(2),
          COMP: (data.cdR_COMP * 100).toFixed(2),
          AGG: (data.cdR_AGG * 100).toFixed(2),
          BTC: (data.cdR_BTC * 100).toFixed(2),
          $SPY: data.cG_SPY.toFixed(2),
          $COMP: data.cG_COMP.toFixed(2),
          $AGG: data.cG_AGG.toFixed(2),
          $BTC: data.cG_BTC.toFixed(2),
          portfolio1: (data.cdR_Portfolio * 100).toFixed(2),
          portfolio2: data.cG_Portfolio.toFixed(2),
          fakeText: 0,
        });
        currentMonth = format(new Date(data.date), "MM/yyyy");
        if (
          Math.min(
            (data.cdR_Portfolio * 100).toFixed(2),
            (data.cdR_BTC * 100).toFixed(2),
            (data.cdR_AGG * 100).toFixed(2),
            (data.cdR_COMP * 100).toFixed(2),
            (data.cdR_SPY * 100).toFixed(2)
          ) < tempMinPercentage
        ) {
          tempMinPercentage = Math.min(
            (data.cdR_Portfolio * 100).toFixed(2),
            (data.cdR_BTC * 100).toFixed(2),
            (data.cdR_AGG * 100).toFixed(2),
            (data.cdR_COMP * 100).toFixed(2),
            (data.cdR_SPY * 100).toFixed(2)
          );
        }
        if (
          Math.min(
            data.cG_Portfolio.toFixed(2),
            data.cG_BTC.toFixed(2),
            data.cG_AGG.toFixed(2),
            data.cG_COMP.toFixed(2),
            data.cG_SPY.toFixed(2)
          ) < tempMinDollar
        ) {
          tempMinDollar = Math.min(
            data.cG_Portfolio.toFixed(2),
            data.cG_BTC.toFixed(2),
            data.cG_AGG.toFixed(2),
            data.cG_COMP.toFixed(2),
            data.cG_SPY.toFixed(2)
          );
        }
        if (
          Math.max(
            (data.cdR_Portfolio * 100).toFixed(2),
            (data.cdR_BTC * 100).toFixed(2),
            (data.cdR_AGG * 100).toFixed(2),
            (data.cdR_COMP * 100).toFixed(2),
            (data.cdR_SPY * 100).toFixed(2)
          ) > tempMaxPercentage
        ) {
          tempMaxPercentage = Math.max(
            (data.cdR_Portfolio * 100).toFixed(2),
            (data.cdR_BTC * 100).toFixed(2),
            (data.cdR_AGG * 100).toFixed(2),
            (data.cdR_COMP * 100).toFixed(2),
            (data.cdR_SPY * 100).toFixed(2)
          );
        }
        if (
          Math.max(
            data.cG_Portfolio.toFixed(2),
            data.cG_BTC.toFixed(2),
            data.cG_AGG.toFixed(2),
            data.cG_COMP.toFixed(2),
            data.cG_SPY.toFixed(2)
          ) > tempMaxDollar
        ) {
          tempMaxDollar = Math.max(
            data.cG_Portfolio.toFixed(2),
            data.cG_BTC.toFixed(2),
            data.cG_AGG.toFixed(2),
            data.cG_COMP.toFixed(2),
            data.cG_SPY.toFixed(2)
          );
        }
      });
      setYTicksForDailyReturn(yTickForReturn);
      setBenchmarkDataState(tempData);
      setMinPercentage(tempMinPercentage);
      setMaxPercentage(tempMaxPercentage);
      setMinDollar(tempMinDollar);
      setMaxDollar(tempMaxDollar);
    } else {
      setBenchmarkDataState(benchmarkData);
    }
  }, [benchmarkData]);

  useEffect(() => {
    if (accountOverview) {
      let temp = accountOverview;
      const monthNames = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ];

      temp?.graphValues.forEach((data) => {
        data.dateTime = new Date(data.dateTime).toLocaleDateString();
        // monthNames[new Date(data.dateTime).getMonth()] +
        // " " +
        // new Date(data.dateTime).getFullYear();
      });
      setAccountOverviewState(temp);
    }
  }, [accountOverview]);

  const CustomTooltipForPercentage = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip">
          <div className="date">{payload[0].payload.fullDate}</div>
          {payload.map((data) => (
            <>
              {data.name === "portfolio2" || data.name === "portfolio1" ? (
                <p className="labelPortfolio">
                  <div className="circle">&nbsp;</div>
                  <div className="value">{`${data.value}%`}</div>
                </p>
              ) : (
                <p className="label">{`${getFullIndexName(data.name)} = ${
                  data.value
                }%`}</p>
              )}
            </>
          ))}
        </div>
      );
    }
    return null;
  };

  const CustomTooltipForDollar = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip">
          <div className="date">{payload[0].payload.fullDate}</div>
          {payload.map((data) => (
            <>
              {data.name === "portfolio2" || data.name === "portfolio1" ? (
                <p className="labelPortfolio">
                  <div className="circle">&nbsp;</div>
                  <div className="value">{`$${parseInt(data.value)
                    .toString()
                    .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`}</div>
                </p>
              ) : (
                <p className="label">{`${getFullIndexName(
                  data.name
                )} = $${parseInt(data.value)
                  .toString()
                  .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`}</p>
              )}
            </>
          ))}
        </div>
      );
    }
    return null;
  };

  const getFullIndexName = (shortIndexName) => {
    switch (shortIndexName) {
      case "$SPY":
      case "SPY":
        return "S&P 500";
      case "$BTC":
      case "BTC":
        return "Bitcoin";
      case "$AGG":
      case "AGG":
        return "Bloomberg Aggregate Bond";
      case "$COMP":
      case "COMP":
        return "NASDAQ";
    }
  };

  const renderColorfulLegendText = (value) => {
    switch (value) {
      case "fakeText":
        return <span style={{ color: "black" }}>Toggle Benchmark:</span>;
      case "portfolio1":
      case "portfolio2":
        return (
          <span className="legendDisabled" style={{ color: "black" }}>
            My Portfolio
          </span>
        );
      case "$SPY":
      case "SPY":
        return (
          <span
            onClick={() => setIsSnp500Visible(!isSnp500Visible)}
            style={{ color: "black" }}
          >
            S&P 500
          </span>
        );
      case "$BTC":
      case "BTC":
        return (
          <span
            onClick={() => setIsBitcoinVisible(!isBitcoinVisible)}
            style={{ color: "black" }}
          >
            Bitcoin{" "}
          </span>
        );
      case "$AGG":
      case "AGG":
        return (
          <span
            onClick={() => setIsBloombergVisible(!isBloombergVisible)}
            style={{ color: "black" }}
          >
            Bloomberg Aggregate Bond{" "}
          </span>
        );
      case "$COMP":
      case "COMP":
        return (
          <span
            onClick={() => setIsNasdaqVisibleVisible(!isNasdaqVisible)}
            style={{ color: "black" }}
          >
            NASDAQ
          </span>
        );
    }
  };

  const handleFilterClick = (option) => {
    setSelectedFilter(option === selectedFilter ? "" : option);
    dispatch({ type: SET_LOADING, payload: true });
    setIsLoaded(false);
    dispatch(getBenchmarkData(option === selectedFilter ? "default" : option));
  };

  return (
    <Wrapper>
      {(!isLoaded || loading) && (
        <LoadingBar isLoading={loading} setIsLoaded={setIsLoaded} />
      )}
      <div className="infoSection">
        <div className="heading">Account Overview</div>
        <Card isMarginNeeded={false}>
          <div className="accountOverViewBody">
            <div className="information">
              <div className="assetRoiInformation">
                <div className="asset">
                  <div className="number">
                    {accountOverviewState?.assetsUnderManagmentAsString}
                  </div>
                  <div className="text">Asset Under Management</div>
                </div>
                <div className="roi">
                  <div
                    className="number"
                    style={{
                      color:
                        accountOverviewState?.roi < 0
                          ? "red"
                          : Math.abs(accountOverviewState?.roi) > 0
                          ? "green"
                          : "black",
                    }}
                  >
                    {accountOverviewState?.roiAsString}
                  </div>
                  <div className="text">ROI Since Inception</div>
                </div>
              </div>
            </div>
          </div>
        </Card>
      </div>
      <div className="chartSection">
        <div className="heading">
          <div>Cumulative Daily Return (%)</div>
          <div>
            <span style={{ fontSize: "15px" }}>Filter: </span>
            <button
              style={{
                border:
                  selectedFilter === "ThreeMonths" ? "1px solid gray" : "none",
              }}
              className="filterButton"
              onClick={() => handleFilterClick("ThreeMonths")}
            >
              3 mo.
            </button>
            <button
              className="filterButton"
              style={{
                border:
                  selectedFilter === "YearToDate" ? "1px solid gray" : "none",
              }}
              onClick={() => handleFilterClick("YearToDate")}
            >
              YTD
            </button>
            <button
              style={{
                border:
                  selectedFilter === "OneYear" ? "1px solid gray" : "none",
              }}
              className="filterButton"
              onClick={() => handleFilterClick("OneYear")}
            >
              1 Year
            </button>
          </div>
        </div>
        <Card lessPadding={window.screen.width < 480 ? true : false}>
          <div className="chart">
            {benchmarkDataState?.length >= 1 ? (
              <ResponsiveContainer
                width="100%"
                height={window.screen.width < 480 ? 120 : 220}
              >
                <LineChart
                  width={500}
                  height={220}
                  data={benchmarkDataState}
                  syncId="anyId"
                  margin={{
                    top: 10,
                    right: 0,
                    left: 0,
                    bottom: 10,
                  }}
                >
                  <Legend
                    wrapperStyle={{ position: "static", marginTop: -10 }}
                    iconType="plainline"
                    formatter={renderColorfulLegendText}
                  />
                  <CartesianGrid vertical={false} />
                  <XAxis dataKey="fullDate" interval="preserveEnd" />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    unit="%"
                    padding={{ bottom: 20 }}
                    domain={[minPercentage, maxPercentage]}
                  />
                  <Tooltip
                    separator=" = "
                    itemStyle={{ color: "black" }}
                    content={<CustomTooltipForPercentage />}
                  />
                  <Line
                    type=""
                    dot={false}
                    dataKey="fakeText"
                    stroke="#33CCFF"
                    fill="#33CCFF"
                    strokeWidth={4}
                    hide={true}
                  />
                  <Line
                    type=""
                    dot={false}
                    dataKey="portfolio1"
                    stroke="#33CCFF"
                    fill="#33CCFF"
                    strokeWidth={4}
                  />
                  <Line
                    type=""
                    dot={false}
                    dataKey="SPY"
                    stroke="#F9572B"
                    fill="#F9572B"
                    strokeWidth={2}
                    hide={isSnp500Visible}
                  />
                  <Line
                    type=""
                    dot={false}
                    dataKey="COMP"
                    stroke="#8F9498"
                    fill="#8F9498"
                    strokeWidth={2}
                    hide={isNasdaqVisible}
                  />
                  <Line
                    type=""
                    dot={false}
                    dataKey="AGG"
                    stroke="#2800D7"
                    fill="#2800D7"
                    strokeWidth={2}
                    hide={isBloombergVisible}
                  />
                  <Line
                    type=""
                    dot={false}
                    dataKey="BTC"
                    stroke="#f7931a"
                    fill="#f7931a"
                    strokeWidth={2}
                    hide={isBitcoinVisible}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="asset">
                <div className="number">
                  We havent found any data for this period
                </div>
              </div>
            )}
          </div>
        </Card>
        <div className="heading">
          Cumulative Growth ($)
          <div>
            <span style={{ fontSize: "15px" }}>Filter: </span>
            <button
              style={{
                border:
                  selectedFilter === "ThreeMonths" ? "1px solid gray" : "none",
              }}
              className="filterButton"
              onClick={() => handleFilterClick("ThreeMonths")}
            >
              3 mo.
            </button>
            <button
              className="filterButton"
              style={{
                border:
                  selectedFilter === "YearToDate" ? "1px solid gray" : "none",
              }}
              onClick={() => handleFilterClick("YearToDate")}
            >
              YTD
            </button>
            <button
              style={{
                border:
                  selectedFilter === "OneYear" ? "1px solid gray" : "none",
              }}
              className="filterButton"
              onClick={() => handleFilterClick("OneYear")}
            >
              1 Year
            </button>
          </div>
        </div>
        <Card lessPadding={window.screen.width < 480 ? true : false}>
          <div className="chart">
            {benchmarkDataState?.length >= 5 ? (
              <ResponsiveContainer
                width="100%"
                height={window.screen.width < 480 ? 120 : 220}
              >
                <LineChart
                  width={500}
                  height={220}
                  data={benchmarkDataState}
                  syncId="anyId"
                  margin={{
                    top: 10,
                    right: 0,
                    left: 0,
                    bottom: 10,
                  }}
                >
                  <Legend
                    wrapperStyle={{ position: "static", marginTop: -10 }}
                    iconType="plainline"
                    formatter={renderColorfulLegendText}
                  />
                  <CartesianGrid vertical={false} />
                  <XAxis dataKey="date" interval={0} />
                  <YAxis
                    domain={[minDollar - 100, maxDollar + 100]}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(value) =>
                      ("$" + parseInt(value)).replace(
                        /\B(?=(\d{3})+(?!\d))/g,
                        ","
                      )
                    }
                  />
                  <Tooltip content={<CustomTooltipForDollar />} />
                  <Line
                    type=""
                    dot={false}
                    dataKey="fakeText"
                    stroke="#33CCFF"
                    fill="#33CCFF"
                    strokeWidth={4}
                    hide={true}
                  />
                  <Line
                    type=""
                    dot={false}
                    dataKey="portfolio2"
                    stroke="#33CCFF"
                    fill="#33CCFF"
                    strokeWidth={4}
                  />
                  <Line
                    type=""
                    dot={false}
                    dataKey="$SPY"
                    stroke="#F9572B"
                    fill="#F9572B"
                    strokeWidth={2}
                    hide={isSnp500Visible}
                  />
                  <Line
                    type=""
                    dot={false}
                    dataKey="$COMP"
                    stroke="#8F9498"
                    fill="#8F9498"
                    strokeWidth={2}
                    hide={isNasdaqVisible}
                  />
                  <Line
                    type=""
                    dot={false}
                    dataKey="$AGG"
                    stroke="#2800D7"
                    fill="#2800D7"
                    strokeWidth={2}
                    hide={isBloombergVisible}
                  />
                  <Line
                    type=""
                    dot={false}
                    dataKey="$BTC"
                    stroke="#f7931a"
                    fill="#f7931a"
                    strokeWidth={2}
                    hide={isBitcoinVisible}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="asset">
                <div className="number">
                  We havent found any data for this period
                </div>
              </div>
            )}
          </div>
        </Card>
      </div>
    </Wrapper>
  );
};

export default AccountOverView;
